<template>
    <div class="col-sm-12">
        <div class="card">
            <div class="card-body">
                <div class="form theme-form">
                    <div v-if="errorMessage" class="alert alert-danger">
                        {{ errorMessage }}
                    </div>

                    <div class="row">
                        <div class="col">
                            <select-box
                                select-id="week-select"
                                label="Select a week"
                                :items="weeks"
                                :item-label="week => week.label"
                                :item-value="week => week.label"
                                :selected-value="weeks.find(week => week.number === selectedWeek)?.label"
                                @update:selectedValue="value => handleSelect('selectedWeek', value)"
                                placeholder="Select a week"
                            />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <select-box
                                :disabled="!selectedWeek || !isEditableWeek"
                                select-id="day-select"
                                label="Select a day"
                                :items="daysOfWeek"
                                :item-label="day => day"
                                :selected-value="selectedDay"
                                @update:selectedValue="value => handleSelect('selectedDay', value)"
                            ></select-box>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <select-box
                                :disabled="!selectedDay || !isEditableWeek"
                                select-id="start-time-select"
                                label="Start Time"
                                :items="availableStartTimes"
                                :item-label="time => time"
                                :selected-value="startTime"
                                @update:selectedValue="value => handleSelect('startTime', value)"
                            ></select-box>
                        </div>
                        <div class="col">
                            <select-box
                                :disabled="!selectedDay || !isEditableWeek"
                                select-id="end-time-select"
                                label="End Time"
                                :items="availableEndTimes"
                                :item-label="time => time"
                                :selected-value="endTime"
                                @update:selectedValue="value => handleSelect('endTime', value)"
                            ></select-box>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <select-box
                                :disabled="!startTime || !endTime"
                                select-id="project-select"
                                label="Select a Project"
                                :items="projects"
                                :item-label="project => project.project_title"
                                :selected-value="selectedProject"
                                @update:selectedValue="value => handleSelect('selectedProject', value)"
                            ></select-box>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import SelectBox from '../Basic/Forms/SelectBox.vue';

export default {
    name: 'WorkTimeCreate',
    components: {
        SelectBox
    },
    props: {
        allowedEditWeeks: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            selectedProject: null,
            selectedWeek: null,
            selectedDay: null,
            startTime: '',
            endTime: '',
            projects: [],
            weeks: this.generateWeeks(),
            daysOfWeek: this.getDaysOfCurrentWeek(),
            allTimes: this.generateTimesList(),
            unavailableTimes: [],
            errorMessage: '',
        };
    },
    computed: {
        isEditableWeek() {
            const currentWeek = this.getCurrentWeekNumber();
            return (
                this.selectedWeek &&
                currentWeek - this.selectedWeek < this.allowedEditWeeks &&
                currentWeek - this.selectedWeek >= 0
            );
        },

        availableStartTimes() {
            return [''].concat(this.allTimes.filter((time) => {
                const timeMinutes = this.timeToMinutes(time);
                const isAvailable = this.unavailableTimes.every((unavailable) => {
                    return timeMinutes < unavailable.start || timeMinutes >= unavailable.end;
                });

                return isAvailable && (!this.endTime || timeMinutes < this.timeToMinutes(this.endTime));
            }));
        },

        availableEndTimes() {
            return [''].concat(this.allTimes.filter((time) => {
                const timeMinutes = this.timeToMinutes(time);
                const isAvailable = this.unavailableTimes.every((unavailable) => {
                    return timeMinutes <= unavailable.start || timeMinutes > unavailable.end;
                });

                return isAvailable && (!this.startTime || timeMinutes > this.timeToMinutes(this.startTime));
            }));
        },

        isCurrentWeek() {
            const currentWeek = this.getCurrentWeekNumber();
            return Number(this.selectedWeek) === currentWeek;
        },

        isAddTimeDisabled() {
            // Блокируем, если не выполнены следующие условия:
            // - Проект не выбран
            // - Неделя не выбрана
            // - День не выбран
            // - Время начала и конца не выбрано
            // - Если редактирование недели не в разрешенном периоде
            return (
                !this.selectedProject ||
                !this.startTime ||
                !this.endTime ||
                !this.selectedDay ||
                !this.isEditableWeek // Добавлена проверка
            );
        },
    },
    methods: {
        emitAddTimeState() {
            this.$emit('updateAddTimeState', this.isAddTimeDisabled);
        },

        handleSelect(propertyName, value) {
            if (propertyName === 'selectedWeek') {
                const selectedWeekObject = this.weeks.find(week => week.label === value);
                this.selectedWeek = selectedWeekObject ? selectedWeekObject.number : null;
            } else {
                this[propertyName] = value;
            }

            this.emitAddTimeState();

            if (propertyName === 'selectedWeek') {
                this.$emit('weekSelected', this.selectedWeek);

                this.daysOfWeek = this.getDaysOfCurrentWeek();
            }

            if (propertyName === 'selectedDay' || propertyName === 'selectedWeek') {
                this.fetchUnavailableTimes();
            }
        },

        clearSelections() {
            this.selectedProject = null;
            this.selectedWeek = null;
            this.selectedDay = null;
            this.startTime = '';
            this.endTime = '';
        },

        generateWeeks() {
            const weeks = [];
            const currentWeek = this.getCurrentWeekNumber();
            const today = new Date();

            for (let i = 0; i < 6; i++) {
                if (currentWeek - i > 0) {
                    const weekNumber = currentWeek - i;
                    const dateRange = this.getWeekDateRange(weekNumber, today.getFullYear());
                    weeks.push({
                        number: weekNumber,
                        label: `Week ${weekNumber}, ${dateRange.start} - ${dateRange.end}`, // Строка "Week X, start - end"
                    });
                }
            }

            return weeks;
        },

        getWeekDateRange(weekNumber, year) {
            const firstDayOfYear = new Date(year, 0, 1); // 1 Января
            const daysOffset = (weekNumber - 1) * 7;
            const weekStart = new Date(firstDayOfYear.getTime() + daysOffset * 24 * 60 * 60 * 1000);

            const dayOfWeek = weekStart.getDay();
            const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            weekStart.setDate(weekStart.getDate() + diffToMonday);

            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6); // Последний день недели (воскресенье)

            return {
                start: this.formatDate(weekStart),
                end: this.formatDate(weekEnd),
            };
        },

        formatDate(date) {
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const day = date.getDate();
            const month = monthNames[date.getMonth()];
            return `${month}${day < 10 ? `.0${day}` : `.${day}`}`;
        },

        getCurrentWeekNumber() {
            const date = new Date();
            const startDate = new Date(date.getFullYear(), 0, 1); // Первый день года
            const daysSinceYearStart = Math.floor((date - startDate) / (24 * 60 * 60 * 1000)) + 1; // Учитываем текущий день
            const startDayOffset = (startDate.getDay() === 0 ? 7 : startDate.getDay()) - 1; // Смещение для выравнивания начальной недели на понедельник
            const adjustedDays = daysSinceYearStart + startDayOffset;

            return Math.ceil(adjustedDays / 7);
        },

        getDaysOfCurrentWeek() {
            const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const today = new Date();
            const currentDayOfWeek = today.getDay() === 0 ? 6 : today.getDay() - 1; // Индекс: понедельник = 0, ..., воскресенье = 6
            const selectedWeekAsNumber = Number(this.selectedWeek);
            const currentWeekNumber = this.getCurrentWeekNumber();

            if (selectedWeekAsNumber === currentWeekNumber) {
                return days.slice(0, currentDayOfWeek + 1);
            }

            // Для других недель — возвращаем полный список
            return days;
        },

        generateTimesList() {
            const times = [];
            for (let hour = 0; hour < 24; hour++) {
                for (let min = 0; min < 60; min += 30) {
                    let displayHour = hour.toString().padStart(2, '0');
                    let displayMin = min.toString().padStart(2, '0');
                    times.push(`${displayHour}:${displayMin}`);
                }
            }
            return times;
        },
        fetchProjects() {
            axios.get('/api/projects')
                .then(response => {
                    this.projects = response.data;
                })
                .catch(error => {
                    console.error('There was an error fetching the projects:', error);
                });
        },

        saveWorkTime() {
            const dayNumber = this.daysOfWeek.findIndex(day => day === this.selectedDay) + 1;

            const payload = {
                project_id: this.selectedProject,
                week: this.selectedWeek,
                day: dayNumber,
                start_time: this.startTime,
                end_time: this.endTime,
            };

            if (this.isTimeConflict(this.startTime, this.endTime)) {
                this.errorMessage = 'The selected time conflicts with unavailable time slots. Please choose another interval.';
                return;
            }

            axios.post('/api/entries', payload)
                .then(response => {
                    this.$emit('workTimeAdded'); // Уведомляем о добавлении
                    // Сбрасываем поля выбора времени и проекта
                    this.startTime = '';
                    this.endTime = '';
                    this.selectedProject = null; // Очищаем выбор проекта
                    this.emitAddTimeState(); // Обновляем состояние кнопки
                    this.errorMessage = ''; // Убираем ошибки (если были)
                    this.fetchUnavailableTimes(); // Обновляем недоступные временные интервалы
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.errorMessage = error.response.data.error || 'An unknown error occurred.';
                    } else {
                        console.error('There was an error saving the work time:', error);
                    }
                });
        },

        fetchUnavailableTimes() {
            if (this.selectedDay && this.selectedWeek) {
                axios
                    .get('/api/entries/times', {
                        params: { day: this.selectedDay, week: this.selectedWeek },
                    })
                    .then((response) => {
                        if (!Array.isArray(response.data)) {
                            console.error("Invalid data format for unavailable times:", response.data);
                            this.unavailableTimes = [];
                            return;
                        }

                        this.unavailableTimes = response.data.map((timeSlot) => {
                            return {
                                start: this.timeToMinutes(timeSlot.start),
                                end: this.timeToMinutes(timeSlot.end),
                                project_title: timeSlot.project_title,
                            };
                        });

                        this.$emit('updateSummary', response.data);
                    })
                    .catch((error) => {
                        console.error("Error fetching unavailable times:", error);
                    });
            }
        },

        timeToMinutes(time) {
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        },

        isTimeConflict(start, end) {
            if (!start || !end) {
                console.error("Invalid input for time conflict check:", { start, end });
                return false; // Если начало или конец не указаны, возвращаем false
            }

            const startMinutes = this.timeToMinutes(start);
            const endMinutes = this.timeToMinutes(end);

            for (let time of this.unavailableTimes) {
                if (!time || typeof time.start !== "number" || typeof time.end !== "number") {
                    console.error("Invalid unavailable time entry:", time);
                    continue; // Пропускаем некорректные записи
                }

                // Проверка на пересечение
                if (
                    (startMinutes >= time.start && startMinutes < time.end) || // Начало внутри
                    (endMinutes > time.start && endMinutes <= time.end) ||    // Конец внутри
                    (startMinutes <= time.start && endMinutes >= time.end)    // Полное покрытие
                ) {
                    return true;
                }
            }
            return false;
        },
    },
}
</script>
